import type { OnboardingV2Variant } from '@/features/flags/legacy-flags';

export enum ErrorType {
  VALIDATION_ERROR = 'validation_error',
  API_ERROR = 'api_error',
  NETWORK_ERROR = 'network_error',
  TIMEOUT = 'timeout',
  PERMISSION_DENIED = 'permission_denied',
  UNKNOWN = 'unknown',
}

export enum ErrorSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  INFO = 'info',
}

export enum OnboardingErrorStep {
  WELCOME = 'welcome',
  MFA = 'mfa',
  ORG_DETAILS = 'org_details',
  CLINICIAN_TYPE = 'clinician_type',
  SESSION_STARTED = 'session_started',
  SIGN_UP_FORM = 'sign_up_form',
  PASSWORD_CREATE = 'password_create',
  CUSTOMIZE_ORGANIZATION = 'customize_organization',
  // Onboarding-v2 / learn-basics flow steps
  ABOUT_YOU = 'about_you',
  MIC_TEST = 'mic_test',
  PROFILE_UPDATE = 'profile_update',
  REGION_UPDATE = 'region_update',
  TEMPLATE_SAVE = 'template_save',
}

export enum OnboardingErrorFlow {
  SCRIBE_LED = 'scribe_led',
  STANDARD = 'standard',
  EVIDENCE = 'evidence',
}

export enum OnboardingWizardVersion {
  V1 = 'v1',
  V2 = 'v2',
}

export enum ErrorSurface {
  TOAST = 'toast',
  BANNER = 'banner',
  MODAL = 'modal',
  INLINE = 'inline',
}

export type OnboardingErrorViewedEventProps = {
  wizard_version?: OnboardingWizardVersion;
  flow?: OnboardingErrorFlow;
  step: OnboardingErrorStep | string;
  error_type: ErrorType | string;
  error_code?: string;
  error_message: string;
  severity: ErrorSeverity;
  http_status_code?: number;
  endpoint?: string;
  country?: string;
  language?: string;
  /** A/B variant the user was bucketed into for the onboarding experiment. */
  onboarding_variant?: OnboardingV2Variant;
};

/** The closed set from `CaptureOrigin::wire_value` in Rust. */
export const DESKTOP_CAPTURE_ORIGINS = ['online', 'offline_recording'] as const;

/** What the desktop IPC error event reports as `capture_origin`. */
export type DesktopCaptureOriginProp =
  | (typeof DESKTOP_CAPTURE_ORIGINS)[number]
  | 'absent'
  | 'unrecognised';

export type ErrorViewedEventProps = {
  surface: ErrorSurface | string;
  screen: string;
  error_type: ErrorType | string;
  error_code?: string;
  error_message: string;
  /** Desktop IPC only: which recorder raised the error. */
  capture_origin?: DesktopCaptureOriginProp;
  /** Desktop IPC only: the Rust message's length; the text itself is never sent. */
  message_length?: number;
  severity: ErrorSeverity;
  http_status_code?: number;
  endpoint?: string;
  country?: string;
  language?: string;
};
